import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import Modal from '@/components/Modal';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { getAuth } from 'firebase/auth';
import { app } from '@/firebase/firebase';
import { buildInviteUrl } from '@/services/inviteService';
import {
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

const CREATE_INVITE_URL = import.meta.env.VITE_CREATE_INVITE_URL;
const DELIVER_INVITE_URL = import.meta.env.VITE_DELIVER_INVITE_URL;


function useAuthHeader() {
  const [header, setHeader] = useState(null);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const auth = getAuth(app);
        const cu = auth.currentUser;
        if (!cu) { setHeader(null); return; }
        const t = await cu.getIdToken();
        if (mounted) setHeader({ Authorization: `Bearer ${t}` });
      } catch {
        setHeader(null);
      }
    })();
    return () => { mounted = false; };
  }, []);
  return header;
}

const EMAIL_RE = /\S+@\S+\.\S+/;

export default function SendInviteModal({
  open,
  onClose,
  team,
}) {
  const authHeader = useAuthHeader();

  // Inputs
  const [email, setEmail] = useState('');

  // Busy / error
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Create-invite result we’ll deliver
  const [inviteUrl, setInviteUrl] = useState('');

  const emailValid = EMAIL_RE.test(email);
  const canSend = useMemo(() => emailValid, [emailValid]);

  // Reset when opened/closed
  useEffect(() => {
    if (!open) {
      setEmail('');
      setInviteUrl('');
      setBusy(false);
      setError('');
    }
  }, [open]);

  // Step 1: create a single-use manager/staff invite
  async function createInviteOnce() {
    if (inviteUrl) return inviteUrl;

    if (!authHeader?.Authorization) {
      throw new Error('Not signed in. Please reauthenticate.');
    }

    const toastId = toast.loading('Creating invite…', { id: 'create-invite' });
    try {
      const baseUrl = (import.meta.env.VITE_APP_BASE_URL || window.location.origin).replace(/\/+$/, '');
      const res = await fetch(CREATE_INVITE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          data: {
            teamId: team?.id,
            audience: 'staff',
            preset: 'manager',
            maxUses: 1,
            expiresInDays: 7,
            baseUrl,
          }
        }),
      });

      const text = await res.text().catch(() => '');
      if (!res.ok) {
        let msg = text || `HTTP ${res.status}`;
        try { msg = JSON.parse(text)?.message || msg; } catch {}
        throw new Error(msg);
      }

      let data = {};
      try { data = JSON.parse(text); } catch {}

      const payload = data?.result || data;
      const code = payload?.code || payload?.inviteCode || payload?.id;
      const url =
        payload?.url ||
        payload?.inviteUrl ||
        payload?.link ||
        (code ? buildInviteUrl(code, team?.id) : '');

      if (!url) throw new Error('No invite url returned.');

      setInviteUrl(url);
      toast.success('Invite created', { id: toastId });
      return url;
    } catch (err) {
      toast.error(err.message || 'Failed to create invite', { id: 'create-invite' });
      throw err;
    }
  }

  // Step 2: deliver via SendGrid
  async function deliver({ method, to, url }) {
    const toastId = toast.loading('Sending email…');
    try {
      const res = await fetch(DELIVER_INVITE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          data: {
            method,        // 'email'
            to,            // address
            inviteUrl: url,
            teamName: team?.name || team?.teamName || 'your team',
          }
        }),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.message || 'Delivery failed');
      }
      toast.success('Email sent!', { id: toastId });
      onClose?.(true);
    } catch (err) {
      setError(err.message || 'Delivery failed');
      toast.error(err.message || 'Delivery failed', { id: toastId });
    }
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!canSend || busy) return;
    setBusy(true);
    setError('');

    try {
      const url = await createInviteOnce();
      await deliver({ method: 'email', to: email.trim(), url });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={() => onClose?.(false)} title={`Send Manager Invite · ${team?.name || ''}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Inputs */}
        <div>
          <label className="block text-sm text-zinc-700 dark:text-zinc-300 mb-1">Recipient email</label>
          <Input
            type="email"
            autoFocus
            placeholder="coach@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={email.length > 0 && !emailValid}
            aria-describedby="email-help"
          />
          {email.length > 0 && !emailValid && (
            <p id="email-help" className="mt-1 text-xs text-red-600">Enter a valid email address.</p>
          )}
        </div>

        {/* Inline error */}
        {error && <p className="text-sm text-red-600">{error}</p>}

        {/* Footer */}
        <div className="mt-2 flex justify-end gap-2">
          <Button plain onClick={() => onClose?.(false)} disabled={busy}>Cancel</Button>
          <Button type="submit" disabled={!canSend || busy}>
            {busy ? 'On it…' : 'Send invite'}
          </Button>
        </div>

        <p className="text-xs text-zinc-500 mt-2">
          We’ll create a single-use manager invite and deliver it via EMAIL.
        </p>
      </form>
    </Modal>
  );
}